import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Injector,
  signal,
  Signal,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BreakpointObserverService, pxToRem } from '@pc/shared';
import { Color, ComponentSizesEnum, PcButtonIcon, PcIcon, PcImages } from '@pc/common';
import { Translations } from './shared/services/translations.service';
import { MainStore } from './store/main-store/main-store';
import { BackgroundMainImageComponent } from '@pc/background-main-image';
import { AppHeaderComponent } from './components/header/app-header.component';
import { AppFooterComponent } from './components/footer/app-footer.component';
import { HeaderModel } from './components/header/header.model';
import { LanguageCode, TranslationService } from '@pc/translation';
import { CompleteProgressStepsDesktop, CompleteProgressStepsMobile, ROUTER_PATHS, StepsRoutes } from './app.const';
import { EnvironmentService } from './environment/environment.service';
import { FooterModel } from './components/footer/footer.model';
import { filter } from 'rxjs';
import { ProgressStepperService } from '@pc/progress-stepper';
import { QuoteOverviewComponent } from './components/quote-overview/quote-overview.component';
import { QuoteOverviewService } from './shared/services/quote/quote-overview.service';
import { TravelersStore } from './core/stores/travelers.store';
import { TripDatesStore } from './pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { DestinationsStore } from './pages/trip-details/where-are-you-travelling/store/destinations.store';
import { ProcessTypeStore } from './store/process-type-store/process-type-store';
import { StepsManagementService } from './shared/services/steps-management.service';
import { SmartlookService } from '@pc/smartlook';
import { AnalyticsService } from './shared/services/analytics.service';
import { AnimationDialogService } from '@pc/dialog-abstract';
import { Dialog } from '@angular/cdk/dialog';
import { SpinningLoaderCardComponent } from './components/spinning-loader-card/spinning-loader-card.component';
import { ProductsStore } from './pages/covers-page/store/products.store';
import { CdkScrollable } from '@angular/cdk/overlay';
import { ProcessSource } from './shared/models/agent/agent-models';

@Component({
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    TranslateModule,
    BackgroundMainImageComponent,
    AppHeaderComponent,
    AppFooterComponent,
    QuoteOverviewComponent,
    CdkScrollable
  ],
  selector: 'pc-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  public isSuccessPage = computed(() => !this.currentUrl().includes(ROUTER_PATHS.SUCCESS_PAGE));
  public headerData = signal<HeaderModel | null>(null);
  public footerData = signal<FooterModel | null>(null);
  public isDesktop: Signal<boolean>;
  public showQuoteOverview = computed(() => this.shouldShowQuoteOverview());
  public showFooter = computed(() => this.shouldShowFooter());
  public bgImage = computed<string>(() => this.getBackgroundImage());
  public quoteOverviewService = inject(QuoteOverviewService);
  public sharedTranslations = inject(TranslationService);
  public elementRef = inject(ElementRef);
  public defaultLang = LanguageCode.EN_AU;
  public mainStore = inject(MainStore);
  public productStore = inject(ProductsStore);
  public translations = inject(Translations);
  public travelersStore = inject(TravelersStore);
  public tripDatesStore = inject(TripDatesStore);
  public destinationsStore = inject(DestinationsStore);
  public processTypeStore = inject(ProcessTypeStore);
  public isDataLoading = computed(() => this.processTypeStore.isLoading() || this.env.isActive() === null);
  public isContinueDisabled = computed(() => !this.stepsManagementService.isStepCompleted() || this.getIsAppLoading());
  public currentUrl = signal(window.location.pathname);
  public showDisclaimer = computed(() => this.currentUrl().includes(ROUTER_PATHS.TRIP_DETAILS));
  public isUnderConstruction = computed(() => this.env.isActive() === false);
  public isNavigationLocked = computed(() => this.mainStore.isNavigationLocked());

  private injector = inject(Injector);
  private breakpoint = inject(BreakpointObserverService);
  private env = inject(EnvironmentService);
  private router = inject(Router);
  private isDevMode = this.env.environment.flags.pureDevMode;
  private headerHeight = 87;
  private progressStepperService = inject(ProgressStepperService);
  private smartlookService = inject(SmartlookService);
  private analytics = inject(AnalyticsService);
  private stepsManagementService = inject(StepsManagementService);
  private ignorePaths = [ROUTER_PATHS.BILLING_ERROR, ROUTER_PATHS.BILLING_SUCCESS];

  private animationDialogService = inject(AnimationDialogService);
  private dialog = inject(Dialog);

  constructor() {
    const initAnalytics = !this.ignorePaths.some((path) => this.currentUrl().includes(path));
    this.isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);

    effect(() => {
      if (this.isDataLoading()) {
        this.animationDialogService.openDialogWithAnimation(this.dialog, SpinningLoaderCardComponent);
      }
    });
    effect(() => this.updateProgressSteps(), { allowSignalWrites: true });
    effect(() => this.listenStepChanged());

    this.initHeaderData();
    this.initFooterData();
    this.initButtonsTexts();
    if (!initAnalytics) return;

    this.trackUrlChange();
    this.initSmartlook();

    this.sharedTranslations.changeLanguage(this.elementRef, this.defaultLang);

    this.analytics.initialAnalyticsProcess();
    this.analytics.initVWOExperiments();
  }

  @HostBinding('style.--header-height') get headerHeightInRem(): string {
    return pxToRem(this.headerHeight) + 'rem';
  }

  shouldShowHeaderDiclamer(): boolean {
    return !(this.processTypeStore.affiliateName() && window.location.pathname !== StepsRoutes.TripDetails);
  }

  public onBckBtnClick(): void {
    this.stepsManagementService.previousStep();
  }

  public onNextBtnClick(): void {
    this.stepsManagementService.nextStep();
  }

  public accStmtClick(): void {
    throw new Error('Method not implemented.');
  }

  private getBackgroundImage(): string {
    return this.isDesktop() ? PcImages.DESKTOP_BACKGROUND_IMAGE_AU : PcImages.MOBILE_BACKGROUND_IMAGE_AU;
  }

  private initButtonsTexts(): void {
    const nextButton = this.translations.instant(this.translations.continue);
    const backbutton = this.translations.instant(
      this.isDesktop() ? this.translations.back.desktop : this.translations.back.mobile
    );

    this.mainStore.setBackButtonText(backbutton);
    this.mainStore.setNextButtonText(nextButton);
  }

  private initHeaderData(): void {
    const hData: HeaderModel = {
      icon: this.isDesktop() ? PcIcon.PASSPORTCARD : PcImages.NAVBAR_MOBILE_LOGO,
      iconSize: ComponentSizesEnum.XXLarge,
      showStepper: this.isSuccessPage(),
      stepperCompletedText: this.sharedTranslations.instant(this.translations.completed),
      logAltText: this.sharedTranslations.instant(this.translations.logoAlt),
      headerHeight: this.headerHeight,
      isDesktop: this.isDesktop,
      stepper: {
        isDevMode: this.isDevMode,
        headerIcon: PcImages.NAVBAR_MOBILE_LOGO,
        accessibilityStatementText: this.sharedTranslations.instant(this.translations.accessibilityStatement),
        steps: this.progressStepperService.progressSteps
      },
      externalLink: this.env.environment.url.websiteUrl
    };
    this.headerData.set(hData);
  }

  private initFooterData(): void {
    const fData: FooterModel = {
      backButtonIcon: PcButtonIcon.CHEVRON_LEFT_BLUE,
      continueButtonIcon: PcButtonIcon.RIGHT_ARROW,
      color: Color.TEXT_COLOR_LINK_BUTTON,
      backButtonText: this.mainStore.backButton,
      continueButtonText: this.mainStore.nextButton,
      isLoading: computed(() => this.getIsAppLoading()),
      shouldShowNextButton: false,
      shouldShowBackButton: false
    };
    this.footerData.set(fData);
  }

  private getIsAppLoading(): boolean {
    return [this.mainStore, this.travelersStore, this.productStore, this.processTypeStore].some((store) =>
      store.isLoading()
    );
  }

  private initSmartlook(): void {
    this.smartlookService.initialize(this.env.environment.analytics.smartlookKey);
    this.smartlookService.record({ ips: true, numbers: true, forms: true });
  }

  private trackUrlChange(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
        const currentPath = event.urlAfterRedirects.split('?')[0];
        this.progressStepperService.navigateToStepByURL(currentPath);
        this.shouldShowBackButton();
        this.shouldShowNextButton();
      }
    });
  }

  private shouldShowBackButton(): void {
    const urlsToIgnore = [ROUTER_PATHS.TRIP_DETAILS];
    const lockedSources = [ProcessSource.BuyNow, ProcessSource.AbandonedCustomer];

    const isBackDisabled = urlsToIgnore.some(
      (url) => this.currentUrl()?.includes(url) || lockedSources.includes(this.processTypeStore.source())
    );

    this.footerData.update((data) => {
      return {
        ...data,
        shouldShowBackButton: !isBackDisabled
      } as FooterModel;
    });
  }

  private shouldShowNextButton(): void {
    const urlsToIgnore: string[] = [ROUTER_PATHS.SECURE_CHECKOUT];
    this.footerData.update((data) => {
      return {
        ...data,
        shouldShowNextButton: !urlsToIgnore.some((url) => this.currentUrl()?.includes(url))
      } as FooterModel;
    });
  }

  private shouldShowFooter(): boolean {
    return !(this.isDesktop() || this.currentUrl().includes(ROUTER_PATHS.SUCCESS_PAGE)) && !!this.env.isActive();
  }

  private updateProgressSteps(): void {
    const progressSteps = this.isDesktop() ? CompleteProgressStepsDesktop : CompleteProgressStepsMobile;
    this.progressStepperService.setProgressSteps(progressSteps);
    this.initHeaderData();
  }

  private shouldShowQuoteOverview(): boolean {
    const includedUrls = [ROUTER_PATHS.COVER_OPTIONS, ROUTER_PATHS.TRAVELER_DETAILS, ROUTER_PATHS.SECURE_CHECKOUT];
    return includedUrls.some((url) => this.currentUrl().includes(url));
  }

  private listenStepChanged(): void {
    this.stepsManagementService.currentStep();
    this.scrollContainer.nativeElement.scrollTo(0, 0);
  }
}
